import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Autocomplete,
  IconButton,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Refresh, Add, Edit, Delete } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { fetchTaxBrackets, createTaxBracket, updateTaxBracket, deleteTaxBracket, fetchTaxConfigurations } from '../../../../../services/salary';
import CustomPagination from '../../../../../components/common/CustomPagination.js';

const TaxBrackets = () => {
  const { t } = useTranslation(['common', 'employees', 'salary']);
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [brackets, setBrackets] = useState([]);
  const [filters, setFilters] = useState({
    tax_configuration: ''
  });
  const [pagination, setPagination] = useState({
    page: 0,
    rowsPerPage: 20,
    totalCount: 0
  });
  const [customRowsInput, setCustomRowsInput] = useState('');
  const [customRowsActive, setCustomRowsActive] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({
    tax_configuration: '',
    min_amount: '',
    max_amount: '',
    tax_rate: '',
    fixed_amount: '0',
    priority: '0'
  });
  const [taxConfigurations, setTaxConfigurations] = useState([]);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const totalPages = Math.ceil(pagination.totalCount / pagination.rowsPerPage) || 1;

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        ...filters,
        page: pagination.page + 1,
        page_size: pagination.rowsPerPage
      };
      
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });

      const response = await fetchTaxBrackets(params);
      
      if (response.results) {
        setBrackets(response.results);
        setPagination(prev => ({
          ...prev,
          totalCount: response.count || 0
        }));
      } else {
        setBrackets(Array.isArray(response) ? response : []);
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to fetch tax brackets');
      setBrackets([]);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.rowsPerPage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const fetchConfigs = async () => {
      try {
        const response = await fetchTaxConfigurations({ page_size: 1000 });
        setTaxConfigurations(response.results || response || []);
      } catch (err) {
        console.error('Failed to fetch tax configurations:', err);
      }
    };
    fetchConfigs();
  }, []);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleRowsPerPageChange = (newRowsPerPage) => {
    setPagination(prev => ({ ...prev, rowsPerPage: newRowsPerPage, page: 0 }));
  };

  const handleCustomRowsInputChange = (value) => {
    setCustomRowsInput(value);
  };

  const handleCustomRowsInputBlur = () => {
    const value = parseInt(customRowsInput);
    if (value && value > 0 && value <= 1000) {
      setPagination(prev => ({ ...prev, rowsPerPage: value, page: 0 }));
      setCustomRowsActive(false);
      setCustomRowsInput('');
    } else {
      setCustomRowsInput('');
      setCustomRowsActive(false);
    }
  };

  const handleOpenModal = (bracket = null) => {
    if (bracket) {
      setEditId(bracket.id);
      setForm({
        tax_configuration: bracket.tax_configuration?.id || bracket.tax_configuration || '',
        min_amount: bracket.min_amount || '',
        max_amount: bracket.max_amount || '',
        tax_rate: bracket.tax_rate || '',
        fixed_amount: bracket.fixed_amount || '0',
        priority: bracket.priority || '0'
      });
    } else {
      setEditId(null);
      setForm({
        tax_configuration: filters.tax_configuration || '',
        min_amount: '',
        max_amount: '',
        tax_rate: '',
        fixed_amount: '0',
        priority: '0'
      });
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditId(null);
    setForm({
      tax_configuration: '',
      min_amount: '',
      max_amount: '',
      tax_rate: '',
      fixed_amount: '0',
      priority: '0'
    });
  };

  const handleFormChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!form.tax_configuration) {
      setSnackbar({ open: true, message: 'Tax configuration is required', severity: 'error' });
      return;
    }

    if (!form.min_amount || parseFloat(form.min_amount) < 0) {
      setSnackbar({ open: true, message: 'Min amount is required and must be >= 0', severity: 'error' });
      return;
    }

    if (form.max_amount && parseFloat(form.max_amount) <= parseFloat(form.min_amount)) {
      setSnackbar({ open: true, message: 'Max amount must be greater than min amount', severity: 'error' });
      return;
    }

    if (!form.tax_rate || parseFloat(form.tax_rate) < 0) {
      setSnackbar({ open: true, message: 'Tax rate is required and must be >= 0', severity: 'error' });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        tax_configuration: form.tax_configuration,
        min_amount: form.min_amount,
        max_amount: form.max_amount || null,
        tax_rate: form.tax_rate,
        fixed_amount: form.fixed_amount || '0',
        priority: parseInt(form.priority) || 0
      };

      if (editId) {
        await updateTaxBracket(editId, payload);
        setSnackbar({ open: true, message: 'Tax bracket updated successfully', severity: 'success' });
      } else {
        await createTaxBracket(payload);
        setSnackbar({ open: true, message: 'Tax bracket created successfully', severity: 'success' });
      }
      
      handleCloseModal();
      fetchData();
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 
                          err.response?.data?.message || 
                          Object.values(err.response?.data || {}).flat().join(', ') ||
                          err.message || 
                          'Failed to save tax bracket';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this tax bracket?')) {
      return;
    }

    try {
      await deleteTaxBracket(id);
      setSnackbar({ open: true, message: 'Tax bracket deleted successfully', severity: 'success' });
      fetchData();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.detail || err.message || 'Failed to delete tax bracket', severity: 'error' });
    }
  };

  const rowsWithSerial = useMemo(() => {
    return brackets.map((item, index) => ({
      ...item,
      sn: pagination.page * pagination.rowsPerPage + index + 1
    }));
  }, [brackets, pagination.page, pagination.rowsPerPage]);

  const columns = useMemo(() => [
    {
      field: 'sn',
      headerName: t('salary:tax.brackets.columns.sn'),
      width: 80,
      sortable: false,
      filterable: false
    },
    {
      field: 'tax_configuration_name',
      headerName: 'Tax Configuration',
      flex: 1,
      minWidth: 200,
      sortable: false,
      renderCell: (params) => params.row.tax_configuration?.name || 'N/A'
    },
    {
      field: 'min_amount',
      headerName: t('salary:tax.brackets.columns.minAmount'),
      flex: 1,
      minWidth: 150,
      sortable: false,
      renderCell: (params) => parseFloat(params.value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    {
      field: 'max_amount',
      headerName: t('salary:tax.brackets.columns.maxAmount'),
      flex: 1,
      minWidth: 150,
      sortable: false,
      renderCell: (params) => {
        if (!params.value) return '∞';
        return parseFloat(params.value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      }
    },
    {
      field: 'tax_rate',
      headerName: t('salary:tax.brackets.columns.taxRate'),
      width: 120,
      sortable: false,
      renderCell: (params) => `${params.value || 0}%`
    },
    {
      field: 'fixed_amount',
      headerName: t('salary:tax.brackets.columns.fixedAmount'),
      flex: 1,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => parseFloat(params.value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    {
      field: 'priority',
      headerName: t('salary:tax.brackets.columns.priority'),
      width: 100,
      sortable: false
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton
            size="small"
            onClick={() => handleOpenModal(params.row)}
            sx={{ color: theme.palette.primary.main }}
          >
            <Edit fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDelete(params.row.id)}
            sx={{ color: theme.palette.error.main }}
          >
            <Delete fontSize="small" />
          </IconButton>
        </Box>
      )
    }
  ], [t, theme]);

  if (loading && brackets.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh',
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#DC143C', mb: 2 }}>
          {t('salary:tax.brackets.title')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchData}
            disabled={loading}
          >
            {t('common:buttons.refresh')}
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenModal()}
            sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' } }}
          >
            {t('salary:tax.brackets.addBracket')}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Autocomplete
                options={taxConfigurations}
                getOptionLabel={(option) => option.name || ''}
                value={taxConfigurations.find(tc => tc.id === parseInt(filters.tax_configuration)) || null}
                onChange={(event, newValue) => {
                  handleFilterChange('tax_configuration', newValue?.id || '');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Tax Configuration"
                    placeholder="Filter by tax configuration"
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ 
            width: '100%',
            overflowX: 'auto',
            minHeight: 520
          }}>
            <DataGrid
              rows={rowsWithSerial}
              columns={columns}
              disableSelectionOnClick
              loading={loading}
              hideFooterPagination
              hideFooter
              getRowSpacing={() => ({ top: 2, bottom: 2 })}
              sx={{
                border: 'none',
                minWidth: 1200,
                '& .MuiDataGrid-columnHeader': {
                  backgroundColor: theme.palette.primary.main,
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '1rem'
                },
                '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
                  color: 'white'
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: '#FFF0F3'
                },
                '& .MuiDataGrid-cell': {
                  fontSize: '1rem'
                },
                '& .MuiDataGrid-row': {
                  marginBottom: '4px',
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>

      <Box sx={{ mt: 3 }}>
        <CustomPagination
          page={pagination.page}
          totalPages={totalPages}
          rowsPerPage={pagination.rowsPerPage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          customRowsActive={customRowsActive}
          customRowsInput={customRowsInput}
          onCustomRowsInputChange={handleCustomRowsInputChange}
          onCustomRowsInputBlur={handleCustomRowsInputBlur}
        />
      </Box>

      {/* Add/Edit Tax Bracket Modal */}
      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editId ? t('common:buttons.edit') + ' ' + t('salary:tax.brackets.title') : t('salary:tax.brackets.addBracket')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <Autocomplete
                  options={taxConfigurations}
                  getOptionLabel={(option) => option.name || ''}
                  value={taxConfigurations.find(tc => tc.id === parseInt(form.tax_configuration)) || null}
                  onChange={(event, newValue) => {
                    handleFormChange('tax_configuration', newValue?.id || '');
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Tax Configuration"
                      required
                      placeholder="Select tax configuration"
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label={t('salary:tax.brackets.columns.minAmount')}
                  type="number"
                  value={form.min_amount}
                  onChange={(e) => handleFormChange('min_amount', e.target.value)}
                  required
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: '0.01', min: '0' }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label={t('salary:tax.brackets.columns.maxAmount')}
                  type="number"
                  value={form.max_amount}
                  onChange={(e) => handleFormChange('max_amount', e.target.value)}
                  placeholder="Leave empty for highest bracket"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: '0.01', min: '0' }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label={t('salary:tax.brackets.columns.taxRate')}
                  type="number"
                  value={form.tax_rate}
                  onChange={(e) => handleFormChange('tax_rate', e.target.value)}
                  required
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: '0.01', min: '0', max: '100' }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label={t('salary:tax.brackets.columns.fixedAmount')}
                  type="number"
                  value={form.fixed_amount}
                  onChange={(e) => handleFormChange('fixed_amount', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: '0.01', min: '0' }}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label={t('salary:tax.brackets.columns.priority')}
                  type="number"
                  value={form.priority}
                  onChange={(e) => handleFormChange('priority', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: '0' }}
                  helperText="Lower number = higher priority"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} disabled={saving}>
            {t('common:buttons.cancel')}
          </Button>
          <Button 
            onClick={handleSave} 
            variant="contained"
            disabled={saving}
            sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' } }}
          >
            {saving ? <CircularProgress size={20} /> : (editId ? t('common:buttons.update') : t('common:buttons.create'))}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TaxBrackets;

